import {
  createTemplateFromEventStudioEventTemplate,
  duplicateStudioEventTemplate,
  StudioEventTemplate,
  EventTemplateFromEventCreate,
  deleteStudioEventTemplate,
  partialUpdateStudioEventTemplate,
  PartialUpdateStudioEventTemplateOkResponse,
  retrieveStudioEventTemplate,
  createStudioEventTemplate,
  CreateStudioEventTemplateOkResponse,
  listStudioEventTemplate,
  listStudioEmailConfigTemplate,
  PartialUpdateStudioEventTemplateRequestBody,
  retrieveStudioIntegrationTemplate,
  StudioIntegrationTemplate,
  partialUpdateStudioIntegrationTemplate,
  PartialUpdateStudioIntegrationTemplateRequestBody,
  createPingStudioIntegrationTemplate,
  listPingsStudioIntegrationTemplate,
  updatePingStudioIntegrationTemplate,
  deletePingStudioIntegrationTemplate,
  listWebhooksStudioIntegrationTemplate,
  createWebhookStudioIntegrationTemplate,
  updateWebhookStudioIntegrationTemplate,
  deleteWebhookStudioIntegrationTemplate,
  getFieldMappingsStudioIntegrationTemplate,
  fetchTemplateFormIoFieldsStudioEventTemplate,
  createFieldMappingStudioIntegrationTemplate,
  fetchFormIoFieldsV2FieldMappings,
  listFieldMappings,
  CrmField
} from '@goldcast/api/backend';
import logger from '@goldcast/logger';

import { FilterBy, TemplateNotification, TemplateWebhook } from './types';
import { transformServerTemplates } from './utils';

import { SourceField } from '@/studio/orgIntegration/store/types';
import { parseErrorFromServer, parseFieldMappingRequestBody } from '@/studio/orgIntegration/store/utils';

const sortByMap = {
  0: {
    ordering: 'title'
  },
  1: {
    ordering: '-title'
  },
  2: {
    ordering: 'created_at'
  },
  3: {
    ordering: '-created_at'
  }
};

const filterByMap = {
  [FilterBy.AllTemplates]: null,
  [FilterBy.OrganizationTemplates]: true,
  [FilterBy.WorkspaceTemplates]: false
};

const actions = {
  async loadTemplates({ dispatch, commit, state }, { workspaceId = '' }) {
    const { sortBy, searchTerm, currentPage, templatesPerPage, filterBy } = state;

    commit('setIsLoading', true);

    return new Promise((resolve, reject) => {
      listStudioEventTemplate({
        queryParams: {
          search: searchTerm,
          offset: (currentPage - 1) * templatesPerPage,
          limit: templatesPerPage,
          workspace: workspaceId,
          workspace__isnull: filterByMap[filterBy],
          ...sortByMap[sortBy]
        }
      })
        .then(templatesData => {
          // @ts-ignore
          const transformedTemplates = transformServerTemplates(templatesData.results);
          commit('setTemplates', transformedTemplates);
          commit('setTemplatesCount', templatesData.count);
          resolve(transformedTemplates);
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: 'Error Loading Templates, Please Try Again', mode: 'error' },
            { root: true }
          );
          reject(error);
        })
        .finally(() => {
          commit('setIsLoading', false);
        });
    });
  },
  async loadTemplatesForInfinteScroll(
    { dispatch, commit },
    { offset = 0, limit = 25, ordering = '-updated_at' }: { offset?: number; limit?: number; ordering?: string }
  ) {
    return new Promise((resolve, reject) => {
      listStudioEventTemplate({ queryParams: { limit, offset, ordering } })
        .then(templatesData => {
          const transformedTemplates = transformServerTemplates(templatesData?.results || []);
          commit('setTemplatesCount', templatesData.count);
          resolve(transformedTemplates);
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: 'Error Loading Templates, Please Try Again', mode: 'error' },
            { root: true }
          );
          reject(error);
        })
        .finally(() => {
          commit('setIsLoading', false);
        });
    });
  },
  createTemplate({ commit, rootGetters }, params) {
    const orgId: string = rootGetters['org/getCurrentOrgId'];
    const payload = {
      ...params,
      organization: orgId
    };
    return new Promise((resolve, reject) => {
      createStudioEventTemplate({
        body: payload
      })
        .then((newTemplate: CreateStudioEventTemplateOkResponse) => {
          // @ts-ignore
          const template = transformServerTemplates([newTemplate]);
          commit('createTemplate', template);
          resolve(newTemplate);
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  createTemplateFromEvent({ commit }, payload) {
    return new Promise((resolve, reject) => {
      createTemplateFromEventStudioEventTemplate({
        body: payload
      })
        .then((newTemplate: EventTemplateFromEventCreate) => {
          const template = transformServerTemplates([newTemplate]);
          commit('createTemplate', template);
          resolve(newTemplate);
        })
        .catch(error => {
          reject(error);
        });
    });
  },
  async updateTemplate(
    context,
    {
      templateId,
      templateDetails
    }: { templateId: string; templateDetails: PartialUpdateStudioEventTemplateRequestBody }
  ) {
    try {
      const updatedTemplate: PartialUpdateStudioEventTemplateOkResponse = await partialUpdateStudioEventTemplate({
        id: templateId,
        body: templateDetails
      });
      context.commit('updateTemplate', updatedTemplate);
    } catch (error) {
      const toastMessage = {
        msg: 'Error: Failed to update Template!',
        mode: 'error'
      };
      context.dispatch('studioToast/triggerToast', toastMessage, {
        root: true
      });
    }
  },
  deleteTemplate({ commit }, templateId): Promise<void> {
    return new Promise((resolve, reject) => {
      deleteStudioEventTemplate({
        id: templateId
      })
        .then(() => {
          commit('removeTemplateFromStore', templateId);
          resolve();
        })
        .catch(reject);
    });
  },
  async loadTemplateById({ dispatch, commit }, templateId) {
    commit('setIsLoading', true);
    try {
      const template = await retrieveStudioEventTemplate({
        id: templateId
      });
      commit('updateTemplates', transformServerTemplates([template]));
    } catch (error) {
      dispatch(
        'studioToast/triggerToast',
        { msg: 'Error Loading Template, Please Try Again', mode: 'error' },
        { root: true }
      );
    } finally {
      commit('setIsLoading', false);
    }
  },
  fetchTemplateRegBuilderData(_, builderUrl) {
    if (builderUrl) {
      return fetch(builderUrl).then(res => res.text());
    }
    return null;
  },
  async loadRegistrationEmailConfig({ dispatch, commit }, templateId) {
    commit('setEmailTemplateLoading', true);
    try {
      const templateEmailConfig = await listStudioEmailConfigTemplate({
        queryParams: {
          email_type: 'Registration',
          event_template: templateId
        }
      });
      const emailConfig = templateEmailConfig[0];
      commit('setRegistrationEmail', { registrationEmail: emailConfig });
    } catch (error) {
      dispatch(
        'studioToast/triggerToast',
        { msg: 'Error Loading Forms, Please Try Again', mode: 'error' },
        { root: true }
      );
    } finally {
      commit('setEmailTemplateLoading', false);
    }
  },
  async duplicateTemplate(
    { commit },
    { templateId, title, workspace }: { templateId: string; title: string; workspace?: string }
  ) {
    return new Promise((resolve, reject) => {
      duplicateStudioEventTemplate({
        id: templateId,
        body: {
          title,
          workspace
        }
      })
        .then((newTemplate: StudioEventTemplate) => {
          const template = transformServerTemplates([newTemplate]);
          commit('createTemplate', template);
          commit('setCurrentTemplateId', newTemplate.id);
          resolve(newTemplate.id);
        })
        .catch(reject);
    });
  },

  async fetchCurrentIntegrationTemplate({ commit, dispatch }, integrationTemplateId: string) {
    commit('setLoadingIntegrationTemplate', true);
    try {
      const response: StudioIntegrationTemplate = await retrieveStudioIntegrationTemplate({
        id: integrationTemplateId
      });
      commit('setCurrentIntegrationTemplate', response);
    } catch (error: any) {
      const toastMessage = {
        msg: 'Failed to fetch integration template!',
        mode: 'error'
      };
      dispatch('studioToast/triggerToast', toastMessage, {
        root: true
      });
      logger.error('[templateList/actions] fetchCurrentIntegrationTemplate failed', error);
    } finally {
      commit('setLoadingIntegrationTemplate', false);
    }
  },

  async updateTemplateIntegration(
    context,
    integrationTemplateDetails: PartialUpdateStudioIntegrationTemplateRequestBody
  ) {
    return new Promise((resolve, reject) => {
      partialUpdateStudioIntegrationTemplate({
        id: context.state.currentIntegrationTemplate?.id,
        body: integrationTemplateDetails
      })
        .then((updatedIntegrationTemplate: StudioIntegrationTemplate) => {
          context.commit('updateIntegrationTemplate', updatedIntegrationTemplate);
          resolve(updatedIntegrationTemplate);
        })
        .catch((error: any) => {
          logger.error('[templateList/actions] fetchCurrentIntegrationTemplate failed', error);
          reject(error);
        })
        .finally(() => {
          context.commit('setIsIntegrationTemplateUpdating', false);
        });
    });
  },

  fetchTemplateNotifications(context) {
    context.commit('setTemplateNotificationsLoading', true);
    const integrationTemplateId = context.state.currentIntegrationTemplate?.id;
    return new Promise((resolve, reject) => {
      listPingsStudioIntegrationTemplate({ id: integrationTemplateId })
        .then(notifications => {
          context.commit('setTemplateNotifications', notifications);
          resolve(notifications);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          context.commit('setTemplateNotificationsLoading', false);
        });
    });
  },
  createTemplateNotification(context, notificationData: Partial<TemplateNotification>) {
    return new Promise((resolve, reject) => {
      context.commit('setTemplateNotificationsLoading', true);
      const integrationTemplateId = context.state.currentIntegrationTemplate?.id;
      createPingStudioIntegrationTemplate({
        id: integrationTemplateId,
        // @ts-ignore
        body: notificationData
      })
        .then(newNotification => {
          context.commit('setTemplateNotifications', [newNotification, ...context.state.templateNotifications]);
          resolve(newNotification);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setTemplateNotificationsLoading', false);
        });
    });
  },
  updateTemplateNotification(context, notificationData: Partial<TemplateNotification>) {
    return new Promise((resolve, reject) => {
      context.commit('setTemplateNotificationsLoading', true);
      const integrationTemplateId = context.state.currentIntegrationTemplate?.id;
      updatePingStudioIntegrationTemplate({
        id: integrationTemplateId,
        ping_id: notificationData.id as string,
        // @ts-ignore
        body: notificationData
      })
        .then(updatedNotification => {
          const updatedNotifications = context.state.templateNotifications.map(notification =>
            notification.id === updatedNotification.id ? updatedNotification : notification
          );
          context.commit('setTemplateNotifications', updatedNotifications);
          resolve(updatedNotification);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setTemplateNotificationsLoading', false);
        });
    });
  },
  deleteTemplateNotification(context, notificationId: string) {
    return new Promise((resolve, reject) => {
      context.commit('setTemplateNotificationsLoading', true);
      const integrationTemplateId = context.state.currentIntegrationTemplate?.id;
      deletePingStudioIntegrationTemplate({ id: integrationTemplateId, ping_id: notificationId })
        .then(() => {
          const updatedNotification = context.state.templateNotifications.filter(
            notification => notification.id !== notificationId
          );
          context.commit('setTemplateNotifications', updatedNotification);
          resolve(updatedNotification);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setTemplateNotificationsLoading', false);
        });
    });
  },
  fetchTemplateWebhooks(context) {
    context.commit('setTemplateWebhooksLoading', true);
    const integrationTemplateId = context.state.currentIntegrationTemplate?.id;
    return new Promise((resolve, reject) => {
      listWebhooksStudioIntegrationTemplate({ id: integrationTemplateId })
        .then(webhooks => {
          context.commit('setTemplateWebhooks', webhooks);
          resolve(webhooks);
        })
        .catch(error => {
          reject(error);
        })
        .finally(() => {
          context.commit('setTemplateWebhooksLoading', false);
        });
    });
  },
  createTemplateWebhook(context, webhookData: Partial<TemplateWebhook>) {
    return new Promise((resolve, reject) => {
      context.commit('setTemplateWebhooksLoading', true);
      const integrationTemplateId = context.state.currentIntegrationTemplate?.id;
      createWebhookStudioIntegrationTemplate({
        id: integrationTemplateId,
        // @ts-ignore
        body: webhookData
      })
        .then(newWebhook => {
          context.commit('setTemplateWebhooks', [newWebhook, ...context.state.templateWebhooks]);
          resolve(newWebhook);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setTemplateWebhooksLoading', false);
        });
    });
  },
  updateTemplateWebhook(context, webhookData: Partial<TemplateWebhook>) {
    return new Promise((resolve, reject) => {
      context.commit('setTemplateWebhooksLoading', true);
      const integrationTemplateId = context.state.currentIntegrationTemplate?.id;
      updateWebhookStudioIntegrationTemplate({
        id: integrationTemplateId,
        webhook_id: webhookData.id as string,
        // @ts-ignore
        body: webhookData
      })
        .then(updatedNotification => {
          const updatedWebhooks = context.state.templateWebhooks.map(notification =>
            notification.id === updatedNotification.id ? updatedNotification : notification
          );
          context.commit('setTemplateWebhooks', updatedWebhooks);
          resolve(updatedNotification);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setTemplateWebhooksLoading', false);
        });
    });
  },
  deleteTemplateWebhook(context, webhookId: string) {
    return new Promise((resolve, reject) => {
      context.commit('setTemplateWebhooksLoading', true);
      const integrationTemplateId = context.state.currentIntegrationTemplate?.id;
      deleteWebhookStudioIntegrationTemplate({ id: integrationTemplateId, webhook_id: webhookId })
        .then(() => {
          const updatedWebhooks = context.state.templateWebhooks.filter(webhook => webhook.id !== webhookId);
          context.commit('setTemplateWebhooks', updatedWebhooks);
          resolve(updatedWebhooks);
        })
        .catch(reject)
        .finally(() => {
          context.commit('setTemplateWebhooksLoading', false);
        });
    });
  },
  async fetchTemplateFieldMappings(context, { crmObjectType, crmType }) {
    if (context.state.templateFieldMappings[crmObjectType].mappings.length !== 0) {
      return;
    }
    context.commit('setTemplateFieldMappingLoading', {
      crmObjectType,
      isFieldMappingLoading: true
    });
    try {
      const integrationTemplateId = context.state.currentIntegrationTemplate?.id;
      const registrationFormInfo = context.getters['getTemplateRegistrationFormInfo'];
      let templateFieldMappings: CrmField[] = [];
      if (registrationFormInfo?.is_global_form) {
        // @ts-ignore
        templateFieldMappings = await listFieldMappings({
          queryParams: {
            crm_type: crmType,
            crm_object_type: crmObjectType,
            is_custom_field: false,
            is_active: true,
            entity_id: registrationFormInfo?.id,
            entity_type: 'GLOBAL_FORM'
          }
        });
      } else {
        // @ts-ignore
        templateFieldMappings = await getFieldMappingsStudioIntegrationTemplate({
          id: integrationTemplateId,
          queryParams: {
            crm_type: crmType,
            crm_object_type: crmObjectType
          }
        });
      }

      templateFieldMappings = templateFieldMappings.sort((a, b) =>
        a.is_default === b.is_default ? 0 : a.is_default ? -1 : 1
      );
      context.commit('setTemplateFieldMappings', { crmObjectType, templateFieldMappings });
      context.commit('setPrevTemplateFieldMappings', {
        crmObjectType,
        prevTemplateFieldMappings: templateFieldMappings
      });
      const defaultErrorArray = new Array(templateFieldMappings.length).fill(null);
      context.commit('setTemplateFieldMappingRowError', {
        crmObjectType,
        errorsArray: defaultErrorArray
      });
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to fetch ${crmType}'s template field mappings for ${crmObjectType}!`,
          mode: 'error'
        },
        { root: true }
      );
      return { error };
    } finally {
      context.commit('setTemplateFieldMappingLoading', {
        crmObjectType,
        isFieldMappingLoading: false
      });
    }
  },
  async fetchTemplateFieldMappingSourceFields(context) {
    context.commit('setTemplateSourceFieldLoading', true);
    try {
      const templateId = context.state.currentTemplateId;
      const registrationFormInfo = context.getters['getTemplateRegistrationFormInfo'];
      let sourceFields: SourceField[] = [];
      if (registrationFormInfo?.is_global_form) {
        //@ts-ignore
        sourceFields = await fetchFormIoFieldsV2FieldMappings({
          queryParams: {
            form_id: registrationFormInfo?.id
          }
        });
      } else {
        //@ts-ignore
        sourceFields = await fetchTemplateFormIoFieldsStudioEventTemplate({
          queryParams: { studio_template_id: templateId }
        });
      }
      context.commit('setTemplateSourceFields', sourceFields);
    } catch (error) {
      context.dispatch(
        'studioToast/triggerToast',
        {
          msg: `Failed to fetch Goldcast's template source fields!`,
          mode: 'error'
        },
        { root: true }
      );
      return { error };
    } finally {
      context.commit('setTemplateSourceFieldLoading', false);
    }
  },
  async saveBulkTemplateFieldMappings(context, crmObjectType) {
    context.commit('setSaveTemplateFieldMappingLoading', {
      crmObjectType,
      isSaveTemplateFieldMappingLoading: true
    });
    const templateFieldMappings = context.state.templateFieldMappings[crmObjectType]?.mappings;
    const templateId = context.state.currentTemplateId;
    const userToken = context.rootState?.user?.trayUserToken;
    const crmType = context.rootState?.orgIntegration?.currentCrmType;
    const solutionInstanceId = context.rootState?.orgIntegration?.currentSolutionInstanceId;
    try {
      await createFieldMappingStudioIntegrationTemplate({
        id: context.state.currentIntegrationTemplate?.id,
        body: {
          // @ts-ignore-next-line
          studio_template: templateId,
          crm_type: crmType,
          crm_object_type: crmObjectType,
          field_mappings: parseFieldMappingRequestBody(templateFieldMappings),
          solution_instance_id: solutionInstanceId
        },
        headers: { 'user-token': userToken.token }
      });
      context.commit('setPrevTemplateFieldMappings', {
        crmObjectType,
        prevTemplateFieldMappings: templateFieldMappings
      });
      context.dispatch(
        'studioToast/triggerToast',
        { msg: 'Template Field mapping created/updated successfully.', mode: 'success' },
        { root: true }
      );
    } catch (error) {
      const parsedError = parseErrorFromServer(error);
      if (parsedError && typeof parsedError === 'string') {
        context.dispatch('studioToast/triggerToast', { msg: parsedError, mode: 'error' }, { root: true });
      } else {
        context.dispatch(
          'studioToast/triggerToast',
          {
            msg: 'Failed to create/update template field mappings! Try again.',
            mode: 'error'
          },
          { root: true }
        );
      }
      if (Array.isArray(parsedError)) {
        context.commit('setTemplateFieldMappingRowError', {
          crmObjectType,
          errorsArray: parsedError
        });
      }
    } finally {
      context.commit('setSaveTemplateFieldMappingLoading', {
        crmObjectType,
        isSaveTemplateFieldMappingLoading: false
      });
    }
  }
};

export default actions;
